import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase } from 'idb';
import type {
  Exercise, ExerciseRevision, Routine, RoutineRevision,
  PlannedSession, ExecutedSession, WorkingSet,
} from '../domain/training.ts';
import type { Recipe, RecipeRevision, PlannedMeal, Consumption, ShoppingItem } from '../domain/nutrition.ts';
import type { WeightObservation } from '../domain/progress.ts';
import type { RestoreOperation } from '../domain/restore.ts';

import {
  failure,
  natural,
  validId,
} from '../domain/daily-routine.ts';

import type {
  CommitReceipt,
  DailyRoutineCheck,
  Result,
} from '../domain/daily-routine.ts';

export interface DatasetControl {
  key: 'control';
  // Logical dataset schema, independent of the physical IndexedDB version (G2 §19).
  schemaVersion: 1;
  activeDatasetId: string;
  controlEpoch: number;
  datasetRevision: number;
  recoveryDatasetId: string | null;
  restoreFence: null | {
    operationId: string;
  };
}

export interface DailyRoutineHead {
  datasetId: string;
  check: DailyRoutineCheck;
}

export interface DailyRoutineReceipt
  extends CommitReceipt {
  datasetId: string;
}

export const FENIX_DATABASE_VERSION = 6;
export const RESTORE_STORES = ['restore_operations'] as const;
export const PROGRESS_STORES = ['progress_weights', 'progress_weight_history', 'progress_receipts'] as const;

export const TRAINING_STORES = [
  'training_exercises',
  'training_exercise_revisions',
  'training_routines',
  'training_routine_revisions',
  'training_planned_sessions',
  'training_planned_session_history',
  'training_executions',
  'training_working_sets',
  'training_receipts',
] as const;
export const NUTRITION_V3_STORES = [
  'nutrition_recipes','nutrition_recipe_revisions','nutrition_planned_meals','nutrition_planned_meal_history',
  'nutrition_consumptions','nutrition_consumption_history','nutrition_shopping_items','nutrition_receipts',
] as const;
export const NUTRITION_V5_STORES = ['nutrition_shopping_item_history'] as const;
export const NUTRITION_STORES = [...NUTRITION_V3_STORES, ...NUTRITION_V5_STORES] as const;

// Dataset-scoped keys: [datasetId, entity/revision/operationId]; plan history
// adds the revision. No date/active constraint until the writer contract is closed.
interface TrainingStore<T> {
  key: [string, string];
  value: { datasetId: string; record: T };
}

export interface FenixSchema extends DBSchema {
  progress_weights: TrainingStore<WeightObservation>;
  progress_weight_history: { key: [string, string, number]; value: { datasetId: string; record: WeightObservation } };
  progress_receipts: TrainingStore<unknown>;
  nutrition_recipes: TrainingStore<Recipe>;
  nutrition_recipe_revisions: TrainingStore<RecipeRevision>;
  nutrition_planned_meals: TrainingStore<PlannedMeal>;
  nutrition_planned_meal_history: { key:[string,string,number]; value:{datasetId:string;record:PlannedMeal} };
  nutrition_consumptions: TrainingStore<Consumption>;
  nutrition_consumption_history: { key:[string,string,number]; value:{datasetId:string;record:Consumption} };
  nutrition_shopping_items: TrainingStore<ShoppingItem>;
  nutrition_shopping_item_history: { key:[string,string,number]; value:{datasetId:string;record:ShoppingItem} };
  nutrition_receipts: TrainingStore<unknown>;
  training_exercises: TrainingStore<Exercise>;
  training_exercise_revisions: TrainingStore<ExerciseRevision>;
  training_routines: TrainingStore<Routine>;
  training_routine_revisions: TrainingStore<RoutineRevision>;
  training_planned_sessions: TrainingStore<PlannedSession>;
  training_planned_session_history: {
    key: [string, string, number];
    value: { datasetId: string; record: PlannedSession };
  };
  training_executions: TrainingStore<ExecutedSession>;
  training_working_sets: {
    key: [string, string];
    value: { datasetId: string; record: WorkingSet; position: number };
  };
  // Receipt payload is deliberately deferred to the idempotent writer block.
  training_receipts: TrainingStore<unknown>;

  restore_operations: {
    key: string;
    value: RestoreOperation;
  };

  control: {
    key: string;
    value: DatasetControl;
  };

  heads: {
    key: [string, string];
    value: DailyRoutineHead;
  };

  history: {
    key: [string, string, number];
    value: DailyRoutineHead;
  };

  receipts: {
    key: [string, string];
    value: DailyRoutineReceipt;
  };
}

export function storageFailure(error: unknown) {
  const name = error instanceof Error
    ? error.name
    : '';

  if (name === 'QuotaExceededError') {
    return failure('STORAGE_QUOTA');
  }

  if (name === 'AbortError') {
    return failure('TRANSACTION_ABORTED');
  }

  if (name === 'VersionError') {
    return failure('SCHEMA_UNSUPPORTED');
  }

  if (name === 'ConstraintError' || name === 'DataError') {
    return failure('CORRUPTION_DETECTED');
  }

  return failure('STORAGE_UNAVAILABLE');
}

export function validDatasetControl(
  control: DatasetControl | undefined,
): control is DatasetControl {
  return !!control
    && control.key === 'control'
    && control.schemaVersion === 1
    && validId(control.activeDatasetId)
    && natural(control.controlEpoch)
    && natural(control.datasetRevision)
    && (
      control.recoveryDatasetId === null
      || validId(control.recoveryDatasetId)
    )
    && (
      control.restoreFence === null
      || validId(control.restoreFence?.operationId)
    );
}

export async function openFenixDatabase(
  dbName: string,
): Promise<Result<IDBPDatabase<FenixSchema>>> {
  try {
    let blocked = false;

    const opened = await new Promise<
      Result<IDBPDatabase<FenixSchema>>
    >((resolve) => {
      openDB<FenixSchema>(dbName, FENIX_DATABASE_VERSION, {
        upgrade(database, oldVersion) {
          if (oldVersion < 1) {
            database.createObjectStore(
              'control',
              { keyPath: 'key' },
            );

            database.createObjectStore('heads');
            database.createObjectStore('history');
            database.createObjectStore('receipts');
          }

          // Additive physical migration: never rewrite G4 records or control.
          if (oldVersion < 2) {
            for (const store of TRAINING_STORES) database.createObjectStore(store);
          }
          // Additive Nutrition migration: G4/Training/control records remain byte-identical.
          if (oldVersion < 3) {
            for (const store of NUTRITION_V3_STORES) database.createObjectStore(store);
          }
          if (oldVersion < 4) {
            for (const store of PROGRESS_STORES) database.createObjectStore(store);
          }
          if (oldVersion < 5) {
            for (const store of NUTRITION_V5_STORES) database.createObjectStore(store);
          }

          // G6 Restore coordination journal.
          // Additive migration only: no v5 user record is rewritten.
          if (oldVersion < 6) {
            for (const store of RESTORE_STORES) {
              database.createObjectStore(store);
            }
          }
        },

        blocked() {
          blocked = true;
          resolve(failure('UPGRADE_BLOCKED'));
        },
      }).then(
        database => {
          if (blocked) {
            database.close();
          } else {
            database.addEventListener('versionchange', () => database.close());
            resolve({
              ok: true,
              value: database,
            });
          }
        },
        error => resolve(storageFailure(error)),
      );
    });

    return opened;
  } catch (error) {
    return storageFailure(error);
  }
}
